import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Send, Copy, Check } from 'lucide-react'
import {
  Button,
  Card,
  IconButton,
  Input,
  PageHeader,
  Select,
  useToast,
} from '@/components/ui'

const EXPIRY_OPTIONS = [
  { value: '1', label: '1 Stunde' },
  { value: '6', label: '6 Stunden' },
  { value: '24', label: '24 Stunden' },
  { value: '48', label: '2 Tage' },
  { value: '168', label: '7 Tage' },
]

export function PasswordSendPage() {
  const [form, setForm] = useState({
    password: '',
    deliveryMethod: 'sms' as 'sms' | 'email',
    recipient: '',
    expiresInHours: 24,
  })
  const [result, setResult] = useState<{ shareUrl: string; expiresAt: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const { toast } = useToast()

  const send = useMutation({
    mutationFn: () => apiClient.post('/passwords/share', form).then((r) => r.data),
    onSuccess: (data) => {
      setResult(data)
      toast({ title: 'Passwort versendet', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Versenden des Passworts', tone: 'danger' })
    },
  })

  const copy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="max-w-lg space-y-6">
      <PageHeader title="Passwort sicher versenden" />

      {result ? (
        <Card className="border-green-200 bg-green-50">
          <Card.Body className="space-y-3">
            <p className="text-green-700 font-semibold">Passwort wurde versendet!</p>
            <p className="text-small text-green-700">Einmal-Link (nur einmal abrufbar):</p>
            <div className="flex items-center gap-2 bg-white rounded-md p-3 border border-green-200">
              <span className="flex-1 text-caption font-mono text-gray-700 break-all">
                {result.shareUrl}
              </span>
              <IconButton
                icon={copied ? <Check className="text-green-600" /> : <Copy />}
                aria-label={copied ? 'Kopiert' : 'Link kopieren'}
                variant="ghost"
                size="sm"
                onClick={copy}
              />
            </div>
            <p className="text-caption text-gray-500">
              Läuft ab: {new Date(result.expiresAt).toLocaleString('de-DE')}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setResult(null)
                setForm({ password: '', deliveryMethod: 'sms', recipient: '', expiresInHours: 24 })
              }}
            >
              Neues Passwort versenden
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="space-y-4">
            <Input
              label="Passwort"
              type="password"
              placeholder="Einzutragendes Passwort"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />

            <Input
              label="Empfänger"
              placeholder={form.deliveryMethod === 'sms' ? '+49 171 1234567' : 'name@firma.de'}
              value={form.recipient}
              onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))}
            />

            <div>
              <label className="block text-small font-medium text-gray-700 mb-1">
                Versandmethode
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['sms', 'email'] as const).map((m) => (
                  <Button
                    key={m}
                    variant={form.deliveryMethod === m ? 'primary' : 'secondary'}
                    onClick={() => setForm((f) => ({ ...f, deliveryMethod: m }))}
                  >
                    {m === 'sms' ? 'SMS' : 'E-Mail'}
                  </Button>
                ))}
              </div>
            </div>

            <Select<string>
              label="Gültig für"
              value={String(form.expiresInHours)}
              onValueChange={(v) => setForm((f) => ({ ...f, expiresInHours: Number(v) }))}
              options={EXPIRY_OPTIONS}
            />

            <Button
              variant="primary"
              fullWidth
              leadingIcon={<Send className="w-4 h-4" />}
              loading={send.isPending}
              disabled={!form.password || !form.recipient || send.isPending}
              onClick={() => send.mutate()}
            >
              {send.isPending ? 'Wird versendet…' : 'Passwort versenden'}
            </Button>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
